import {defineField, defineType} from 'sanity'
import {richTextSimple} from '../../data/richText'
import {PageBlockTypes} from '../pageBlocks'
import AccessorySkusAlt from '../../components/AccessorySkusAlt'
import ProductCombinationInput from '../../components/ProductCombinationInput'
import getPageInfo, {seoGroup} from '../objects/pageInfo'

const shouldHideQuantitySection = (document: any, parent: any) => {
  if (!document?.groupProductQuantities) return false
  const combinations = document.productCombinations || []
  const lastItem = combinations[combinations.length - 1]
  return lastItem._key !== parent._key
}

export default defineType({
  name: 'accessoryBundle',
  title: 'Accessory Bundle',
  type: 'document',
  groups: [
    {
      name: 'content',
      title: 'Content',
      default: true,
    },
    seoGroup,
  ],
  fields: [
    getPageInfo(),
    defineField({
      name: 'bundleName',
      title: 'Bundle Name',
      type: 'string',
      validation: (rule) => rule.required(),
      description: 'Displayed on the page',
      group: 'content',
    }),
    defineField({
      name: 'name',
      title: 'Name',
      type: 'string',
      description:
        'For Sanity Studio internal use, like naming special edge-case bundles like M3 E9x BMWs',
      group: 'content',
    }),
    defineField({
      title: 'Slug',
      name: 'slug',
      type: 'slug',
      description:
        'The URL path for this bundle. This should not be changed after the bundle is published.',
      options: {
        source: 'bundleName',
      },
      validation: (rule) => rule.required(),
      group: 'content',
    }),
    defineField({
      name: 'productCombinations',
      title: 'Product combinations',
      description: 'Each product will be displayed as a dropdown on the page',
      type: 'array',
      validation: (rule) => [rule.min(1), rule.required()],
      group: 'content',
      of: [
        {
          name: 'productCombination',
          title: 'Product combination',
          type: 'object',
          components: {
            input: ProductCombinationInput,
          },
          fields: [
            defineField({
              name: 'fieldLabel',
              title: 'Field Label',
              type: 'string',
              validation: (rule) => rule.required(),
              description: 'Label for the dropdown field',
            }),
            defineField({
              name: 'preselectedProduct',
              title: 'Preselected Product',
              type: 'boolean',
              description:
                'If turned ON, the dropdown will be preselected with the first product option. User will have to select one option.',
              initialValue: false,
            }),
            defineField({
              name: 'excludeFromBundlePriceEstimate',
              title: 'Exclude from bundle price estimate',
              type: 'boolean',
              description:
                'If turned ON, this option/product will be excluded when calculating the (informative) starting price of the bundle.',
              initialValue: false,
            }),
            defineField({
              name: 'defaultQuantity',
              title: 'Default Quantity',
              type: 'string',
              options: {
                list: [
                  {
                    value: 'onePerWheel',
                    title: 'Number of Purchased Wheels',
                  },
                  {
                    value: 'byAxle',
                    title: 'By Axle',
                  },
                  {
                    value: 'byCar',
                    title: 'By Car',
                  },
                  {
                    value: 'custom',
                    title: 'Custom',
                  },
                ],
              },
              initialValue: 'onePerWheel',
              validation: (rule) => rule.required(),
              hidden: ({document, parent}) => shouldHideQuantitySection(document, parent),
            }),
            defineField({
              name: 'defaultQuantityValue',
              title: 'Default Quantity Value',
              type: 'number',
              initialValue: 1,
              hidden: ({document, parent}) => {
                return (
                  shouldHideQuantitySection(document, parent) ||
                  parent?.defaultQuantity !== 'custom'
                )
              },
            }),
            defineField({
              name: 'products',
              title: 'Products to combine together',
              description:
                'User will choose one of the products. Configurable products will be expanded into simple products, each one becoming an option in the dropdown.',
              type: 'array',
              validation: (rule) => [rule.min(1), rule.required()],
              of: [
                {
                  type: 'object',
                  fields: [
                    defineField({
                      name: 'sku',
                      type: 'string',
                    }),
                    defineField({
                      name: 'name',
                      type: 'string',
                    }),
                    defineField({
                      name: 'id',
                      type: 'number',
                    }),
                    defineField({
                      name: 'productType',
                      type: 'string',
                    }),
                    defineField({
                      name: 'customLabel',
                      title: 'Custom Label',
                      type: 'string',
                      description: 'Optional custom label for this product',
                    }),
                  ],
                },
              ],
              components: {
                input: AccessorySkusAlt,
              },
            }),
          ],
        },
      ],
    }),
    defineField({
      name: 'groupProductQuantities',
      title: 'Group product quantities',
      description:
        'Each product will be added to cart with the same selected quantity. Default quantity will be used from the last product in bundle.',
      type: 'boolean',
      group: 'content',
    }),
    defineField({
      name: 'displayKitPrice',
      title: 'Display kit price',
      description:
        'If turned ON, the bundle shows the combined price of the whole kit (every combination at its default quantity) before a selection is made, without the "ea." unit label. Intended for stud kits and other composite kit products sold as a set.',
      type: 'boolean',
      initialValue: false,
      group: 'content',
    }),
    defineField({
      name: 'hideForCenterLockedWheels',
      title: 'Hide for center locked wheels',
      description:
        'When adding accessories for a center locked wheel, bundles with this field active will be hidden.',
      type: 'boolean',
      group: 'content',
    }),
    defineField({
      name: 'unit',
      title: 'Custom unit',
      type: 'string',
      description: 'If empty, unit will be "ea."',
      group: 'content',
    }),
    defineField({
      name: 'photo',
      title: 'Main photo',
      description: 'displayed in listing and cart',
      type: 'image',
      options: {
        hotspot: true,
      },
      group: 'content',
    }),
    defineField({
      name: 'gallery',
      title: 'Gallery',
      type: 'array',
      description:
        'Upload images with a consistent aspect-ratio (all images of the same or similar width/height proportion). Leave at least 10% empty space at each edge of the photo.',
      of: [
        {
          type: 'image',
          options: {
            hotspot: true,
          },
        },
      ],
      group: 'content',
    }),
    defineField({
      name: 'description',
      title: 'Short Description',
      description: '1-2 sentences max',
      type: 'array',
      of: richTextSimple,
      group: 'content',
    }),
    defineField({
      name: 'pageBlocks',
      title: 'Page blocks',
      type: 'array',
      of: PageBlockTypes,
      group: 'content',
    }),
  ],
  preview: {
    select: {
      name: 'name',
      bundleName: 'bundleName',
    },
    prepare({name, bundleName}) {
      return {
        title: name || bundleName || 'Untitled',
        subtitle: 'Accessory Bundle',
      }
    },
  },
})
